import { useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useCurrentUser } from '@/react/hooks/useUser';

import { FormSection } from '@@/form-components/FormSection';

import {
  HelmRegistrySelect,
  RegistryValue,
} from '../components/HelmRegistrySelect';
import { useHelmRegistryRepoOptions } from '../helmChartSourceQueries/useHelmRegistries';
import { useIsRegistryAvailable } from '../upgrade/utils/useIsRegistryAvailable';
import { useHelmChartList } from '../helmChartSourceQueries/useHelmChartList';
import { Chart } from '../types';

import { HelmTemplatesList } from './HelmTemplatesList/HelmTemplatesList';

type Props = {
  namespace: string;
  onChartSelect: (chart: Chart, registry: RegistryValue | null) => void;
};

export function HelmChartSelectionSection({ namespace, onChartSelect }: Props) {
  const environmentId = useEnvironmentId();
  const { user } = useCurrentUser();

  const [selectedRegistry, setSelectedRegistry] =
    useState<RegistryValue | null>(null);

  const registryOptionsQuery = useHelmRegistryRepoOptions(
    environmentId,
    namespace
  );

  const isOCI = selectedRegistry?.registryId !== undefined;
  const chartListQuery = useHelmChartList(
    user.Id,
    environmentId,
    {
      id: selectedRegistry?.registryId,
      url: selectedRegistry?.registryUrl,
    },
    isOCI
  );

  const isRegistryAvailable = useIsRegistryAvailable(
    selectedRegistry,
    registryOptionsQuery.registryOptions
  );

  return (
    <FormSection title="Helm chart">
      <HelmRegistrySelect
        selectedRegistry={selectedRegistry}
        onRegistryChange={setSelectedRegistry}
        namespace={namespace}
        isRegistryAvailable={isRegistryAvailable}
        isLoading={registryOptionsQuery.isLoading}
        isLoadingError={registryOptionsQuery.isError}
        registryOptions={registryOptionsQuery.registryOptions}
      />
      {selectedRegistry && (
        <HelmTemplatesList
          charts={chartListQuery.data ?? []}
          selectAction={(chart) => onChartSelect(chart, selectedRegistry)}
          isLoadingCharts={chartListQuery.isInitialLoading}
          selectedRegistry={selectedRegistry}
          isRegistryError={chartListQuery.isError}
          isOCI={selectedRegistry?.isOCI}
          registryId={selectedRegistry?.registryId}
        />
      )}
    </FormSection>
  );
}
